<template>
  <div>
    <h1>Redirecting...</h1>
    <p>
      If you are not redirected automatically,
      <a
          :href="$route.params.shortLink"
          class="btn btn-link p-0 align-baseline"
      >click here</a>.
    </p>
  </div>
</template>

<script>
import axios from "axios";

const apiUrl = process.env.VUE_APP_API_URL;

export default {
  name: 'ShortRedirect',
  /**
   * Handles the lifecycle method `mounted` in a Vue component. This method is responsible for fetching the original link
   * associated with a short link from the route parameters, analyzing user data (such as operating system, browser,
   * IP address, country, and city), and sending the collected data to an API endpoint. If successful, it redirects the
   * user to the original link; otherwise, it navigates the user to a 404 error page.
   *
   * @return {Promise<void>} Resolves when all required operations are completed, handling user redirection
   *                         or error navigation based on the short link and user data processing.
   */
  async mounted() {
    const shortLink = this.$route.params.shortLink;

    try {
      const response = await axios.get(`${apiUrl}/links/short_link/${shortLink}`);
      const data = response.data.links;
      const originalLink = data[0]?.original_link;

      if (originalLink) {

        /*
        * Analyze user data and send it to the API
         */
        let os = null, browser = null, ip = null, country = null, city = null;
        const id_links = data[0]?.id_links || null;
        const id_user = data[0]?.id_user || null;

        if (!id_links || !id_user) {
          this.$router.push({name: 'Error404'});
          return;
        }

        /*
        * Get user agent information to determine OS and browser
        */
        const userAgent = navigator.userAgent;

        if (userAgent) {
          if (/windows/i.test(userAgent)) os = "Windows";
          else if (/mac/i.test(userAgent)) os = "MacOS";
          else if (/linux/i.test(userAgent)) os = "Linux";
          else if (/android/i.test(userAgent)) os = "Android";
          else if (/iphone|ipad|ipod/i.test(userAgent)) os = "iOS";
          else os = "Unknown"; // Default value

          if (/chrome/i.test(userAgent)) browser = "Chrome";
          else if (/firefox/i.test(userAgent)) browser = "Firefox";
          else if (/safari/i.test(userAgent) && !/chrome/i.test(userAgent)) browser = "Safari";
          else if (/edg/i.test(userAgent)) browser = "Edge";
          else if (/opera|opr/i.test(userAgent)) browser = "Opera";
          else if (/msie|trident/i.test(userAgent)) browser = "IE";
          else browser = "Unknown"; // Default value
        }

        /*
        * Fetch user's IP address, country, and city using an external API
        */
        try {
          const ipRes = await axios.get("https://ipapi.co/json/");
          ip = ipRes.data.ip || null;
          country = ipRes.data.country_name || null;
          city = ipRes.data.city || null;
        } catch (e) {
          // If it fails, leave as null
          this.$router.push({name: 'Error404'});
        }

        /*
        * Save the link visit data to the API
        */
        try {
          await axios.post(`${apiUrl}/linksVisited`, {
            id_links,
            id_user,
            so: os,
            web_navigator: browser,
            ip,
            country,
            city
          });
        } catch (e) {
          // If saving fails, continue anyway
        }

        window.location.replace(originalLink);
      } else {
        this.$router.push({name: 'Error404'});
      }
    } catch (e) {
      this.$router.push({name: 'Error404'});
    }
  }
};
</script>
